/**
 * WorkflowGroupHeader
 * Collapsible header row for a workflow instance group in the Task Scheduler.
 * Shows workflow ID, client name, workflow type, status, SLA deadline, overdue
 * count, and a "View Details" link.
 */

import { ChevronDown } from 'lucide-react';
import styled from 'styled-components';
import { StatusBadge, workflowTypeLabel } from '../../components/tasks';
import type { TaskStatus } from '../../types/tasks';

// ============================================================================
// Types
// ============================================================================

export interface WorkflowGroupHeaderProps {
  workflowInstanceId: string;
  workflowType: string;
  clientName: string | null;
  status: string;
  slaDeadline: string | null;
  taskCount: number;
  overdueCount: number;
  inProgressCount: number;
  isExpanded: boolean;
  onToggle: () => void;
  onViewDetails?: () => void;
}

// ============================================================================
// Helpers
// ============================================================================

function formatDate(isoDate: string | null): string | null {
  if (!isoDate) return null;
  const d = new Date(isoDate);
  if (isNaN(d.getTime())) return null;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

/** Returns the last 6 hex characters of the id as a short reference. */
function shortId(id: string): string {
  if (!id || id === 'standalone') return 'Ad Hoc';
  return id.replace(/-/g, '').slice(-6).toUpperCase();
}

/**
 * Best-effort coercion from workflow instance status to a TaskStatus so we can
 * reuse the existing StatusBadge. Falls back to IN_PROGRESS when not mappable.
 */
function toTaskStatus(status: string): TaskStatus {
  const map: Record<string, TaskStatus> = {
    ACTIVE: 'IN_PROGRESS',
    COMPLETED: 'COMPLETED',
    CANCELLED: 'SKIPPED',
    PENDING: 'PENDING',
    OVERDUE: 'OVERDUE',
  };
  return (map[status.toUpperCase()] as TaskStatus) ?? 'IN_PROGRESS';
}

// ============================================================================
// Styled components
// ============================================================================

// Changed from `button` to `div` to avoid invalid nested-button HTML
// (ViewDetailsButton is a <button> inside this element; nesting buttons is invalid HTML).
const HeaderRow = styled.div<{ $isExpanded: boolean }>`
  width: 100%;
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[3]};
  padding: ${({ theme }) => `${theme.spacing[3]} ${theme.spacing[4]}`};
  background: ${({ theme }) => theme.colors.backgrounds.light};
  border: none;
  border-bottom: 1px solid ${({ theme }) => theme.colors.borders.light};
  cursor: pointer;
  text-align: left;
  transition: background ${({ theme }) => theme.transitions.fast};

  &:hover {
    background: ${({ theme }) => theme.colors.primary.lightSky};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.primary.base};
    outline-offset: -2px;
  }
`;

const ChevronIcon = styled.span<{ $isExpanded: boolean }>`
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
  color: ${({ theme }) => theme.colors.text.secondary};
  transition: transform ${({ theme }) => theme.transitions.base};
  transform: ${({ $isExpanded }) => ($isExpanded ? 'rotate(0deg)' : 'rotate(-90deg)')};
`;

const IdentityGroup = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[2]};
  min-width: 0;
  overflow: hidden;
`;

const WorkflowLabel = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text.primary};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const WorkflowRef = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  color: ${({ theme }) => theme.colors.text.light};
  font-family: monospace;
  white-space: nowrap;
  flex-shrink: 0;
`;

const BadgeGroup = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[2]};
  flex-shrink: 0;
`;

// error.bg (#fef2f2) is slightly lighter than Tailwind red-100 (#fee2e2);
// using the more saturated red-100 for better pill contrast. Text is Tailwind red-800
// which has no exact theme token (theme.colors.status.error.text is #dc2626 = red-600).
const OverdueBadge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 2px 10px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  background: #fee2e2;
  color: #991b1b;
  white-space: nowrap;
`;

const MetaGroup = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[3]};
  flex-shrink: 0;
`;

const SLAText = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  color: ${({ theme }) => theme.colors.text.secondary};
  white-space: nowrap;
`;

const TaskCountText = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  color: ${({ theme }) => theme.colors.text.light};
  white-space: nowrap;
`;

const ViewDetailsButton = styled.button`
  background: none;
  border: none;
  padding: 0;
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  color: ${({ theme }) => theme.colors.primary.base};
  cursor: pointer;
  white-space: nowrap;
  transition: color ${({ theme }) => theme.transitions.fast};
  flex-shrink: 0;

  &:hover {
    color: ${({ theme }) => theme.colors.primary.dark};
    text-decoration: underline;
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.primary.base};
    outline-offset: 2px;
    border-radius: ${({ theme }) => theme.borderRadius.sm};
  }
`;

// ============================================================================
// Component
// ============================================================================

export function WorkflowGroupHeader({
  workflowInstanceId,
  workflowType,
  clientName,
  status,
  slaDeadline,
  taskCount,
  overdueCount,
  isExpanded,
  onToggle,
  onViewDetails,
}: WorkflowGroupHeaderProps) {
  const ref = shortId(workflowInstanceId);
  const displayWorkflowType = workflowTypeLabel(workflowType);
  const displayLabel = clientName
    ? `${clientName} — ${displayWorkflowType}`
    : displayWorkflowType;
  const formattedSLA = formatDate(slaDeadline);

  const handleViewDetailsClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onViewDetails?.();
  };

  return (
    <HeaderRow
      $isExpanded={isExpanded}
      role="button"
      tabIndex={0}
      onClick={onToggle}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onToggle();
        }
      }}
      aria-expanded={isExpanded}
      aria-label={`${displayLabel} workflow group, ${taskCount} tasks`}
    >
      <ChevronIcon $isExpanded={isExpanded} aria-hidden="true">
        <ChevronDown size={16} />
      </ChevronIcon>

      <IdentityGroup>
        <WorkflowRef>#{ref}</WorkflowRef>
        <WorkflowLabel title={displayLabel}>{displayLabel}</WorkflowLabel>
      </IdentityGroup>

      <BadgeGroup>
        <StatusBadge status={toTaskStatus(status)} size="sm" />
        {overdueCount > 0 && (
          <OverdueBadge aria-label={`${overdueCount} overdue tasks`}>
            {overdueCount} Overdue
          </OverdueBadge>
        )}
      </BadgeGroup>

      <MetaGroup>
        {formattedSLA && <SLAText>Due: {formattedSLA}</SLAText>}
        <TaskCountText>{taskCount} task{taskCount !== 1 ? 's' : ''}</TaskCountText>
      </MetaGroup>

      {onViewDetails && (
        <ViewDetailsButton
          onClick={handleViewDetailsClick}
          type="button"
          aria-label={`View details for ${displayLabel}`}
        >
          View Details &rsaquo;
        </ViewDetailsButton>
      )}
    </HeaderRow>
  );
}

export default WorkflowGroupHeader;
